// Entry point for public-facing application
import '@tailwindplus/elements';
import Alpine from 'alpinejs';

declare global {
  interface Window {
    Alpine: typeof Alpine;
  }
}

window.Alpine = Alpine;
Alpine.start();

import { captureException } from './lib/sentry';
import { isNetworkError } from './lib/csrf';
import { registerPasskey, authenticateWithPasskey, PasskeyRejectedError } from './lib/webauthn';
import { initDocSearch } from './lib/docsearch';

// Reload when restored from bfcache. Long-idle Firefox tabs can "revive" with
// dead DOM wrappers that crash React/TanStack Query/Recharts on rehydration.
window.addEventListener('pageshow', (e) => {
  if (e.persisted) window.location.reload();
});

document.addEventListener('DOMContentLoaded', initDocSearch);

// Handle data-confirm on form submissions (no Turbo/UJS in this app)
document.addEventListener('submit', (e) => {
  const submitter = (e as SubmitEvent).submitter as HTMLElement | null;
  const message = submitter?.dataset?.confirm;
  if (message && !confirm(message)) {
    e.preventDefault();
  }
});

// Passkey registration (account settings page)
document.addEventListener('DOMContentLoaded', () => {
  const registerBtn = document.getElementById('register-passkey');
  const labelInput = document.getElementById('passkey-label') as HTMLInputElement | null;
  const errorEl = document.getElementById('passkey-error');
  const modal = document.getElementById('add-passkey-modal') as HTMLDialogElement | null;

  if (registerBtn && labelInput && modal) {
    registerBtn.addEventListener('click', async () => {
      const label = labelInput.value.trim();
      if (!label) {
        labelInput.focus();
        return;
      }

      if (errorEl) errorEl.classList.add('hidden');

      try {
        registerBtn.setAttribute('disabled', 'true');
        registerBtn.textContent = 'Waiting for device...';
        await registerPasskey(label);
        window.location.reload();
      } catch (e) {
        registerBtn.removeAttribute('disabled');
        registerBtn.textContent = 'Add passkey';

        // Connectivity failure: tell the user what to fix, don't page us
        if (isNetworkError(e)) {
          if (errorEl) {
            errorEl.textContent = 'Network error. Check your connection and try again.';
            errorEl.classList.remove('hidden');
          }
          return;
        }

        if (e instanceof Error &&
          e.message !== 'Passkey creation cancelled' &&
          e.name !== 'NotAllowedError') {
          if (errorEl) {
            errorEl.textContent = 'Failed to add passkey. Please try again.';
            errorEl.classList.remove('hidden');
          }
          captureException(e);
        }
      }
    });
  }
});

// Passkey authentication (login page)
document.addEventListener('DOMContentLoaded', () => {
  const loginBtn = document.getElementById('passkey-login');
  if (loginBtn) {
    loginBtn.addEventListener('click', async () => {
      try {
        loginBtn.setAttribute('disabled', 'true');
        loginBtn.textContent = 'Waiting for device...';
        const redirect = await authenticateWithPasskey();
        window.location.href = redirect;
      } catch (e) {
        loginBtn.removeAttribute('disabled');
        loginBtn.textContent = 'Sign in with a passkey';
        const errorDialog = document.getElementById('passkey-login-error') as HTMLDialogElement | null;

        // Connectivity failure: tell the user what to fix, don't page us
        if (isNetworkError(e)) {
          const messageEl = document.getElementById('passkey-login-error-message');
          if (messageEl) messageEl.textContent = 'Check your connection and try again.';
          if (errorDialog) errorDialog.showModal();
          return;
        }

        // Expected security outcome: show the reason, don't page us
        if (e instanceof PasskeyRejectedError) {
          const messageEl = document.getElementById('passkey-login-error-message');
          if (messageEl) messageEl.textContent = e.message;
          if (errorDialog) errorDialog.showModal();
          return;
        }

        if (e instanceof Error &&
          e.message !== 'Passkey authentication cancelled' &&
          e.name !== 'NotAllowedError') {
          if (errorDialog) errorDialog.showModal();
          captureException(e);
        }
      }
    });
  }
});
